// REPANCY-AGENT-LOCK: agent-ux phase-K.6 (until 2026-05-22)
/**
 * K.6 — "Connect my SharePoint sites" panel.
 *
 * Embedded inside Settings → Workspace tab, below the
 * WorkspaceSetupPanel. Lets an evaluator who has their own M365 tenant
 * sign in, list their SharePoint sites, and explicitly grant Repancy
 * read access to one or more.
 *
 * Flow:
 *   1. User signs in with Microsoft (delegated MSAL token with
 *      Sites.Selected + Sites.Read.All).
 *   2. SPA calls Graph `GET /me/followedSites` and `GET /sites?search=*`
 *      to populate a picker.
 *   3. User selects N sites + clicks "Connect".
 *   4. For each selected site, SPA calls Graph
 *      `POST /sites/{siteId}/permissions` to grant the Repancy (dev)
 *      app per-site read access. The grant body specifies our app's
 *      `id` (MS_APP_CLIENT_ID) + roles=["read"].
 *   5. SPA calls Convex `userSelectedSites.recordGrant` with the
 *      resulting `grantId` so we can revoke later.
 *
 * Privacy: Repancy NEVER writes to user's tenant (per K.5 / J.7
 * locked decision). Even with grants, our app-only token reads only.
 *
 * Today this is a SCAFFOLD: the UI is wired; the Graph POST/DELETE
 * calls are stubbed pending K.6 next-pass. The Convex mutations
 * accept records anyway so manual testing + e2e can populate the
 * table without firing real Graph calls.
 *
 * See `packages/repancy/future-plans/PHASE-K-USER-SITE-BROWSING.md`
 * for the full design.
 */

import { useCallback, useEffect, useState, type JSX } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { useUser } from "@clerk/react";
import { api } from "../../../convex/_generated/api";
import { testIds } from "../../lib/testIds";
import {
  acquireGraphToken,
  connectMicrosoftAccount,
  getActiveAccount,
} from "../../lib/graph/msalClient";
import { pushConnectedSitePermissionErrorToast } from "../../lib/graph/useGraphErrorToast";

interface DiscoveredSite {
  id: string;
  displayName: string;
  webUrl: string;
  hostTenant: string;
}

/** Parse a Graph site row into the shape we persist. */
function siteFromGraphRow(
  row: { id?: string; displayName?: string; webUrl?: string },
): DiscoveredSite | null {
  if (!row.id || !row.webUrl || !row.displayName) return null;
  // hostTenant = the bit before the first slash after https://
  try {
    const u = new URL(row.webUrl);
    return {
      id: row.id,
      displayName: row.displayName,
      webUrl: row.webUrl,
      hostTenant: u.host,
    };
  } catch {
    return null;
  }
}

/**
 * Fetch sites visible to the signed-in user via Graph. Tries
 * `/me/followedSites` first (curated), then falls back to
 * `/sites?search=*` (broader).
 */
async function fetchDiscoverableSites(
  token: string,
): Promise<DiscoveredSite[]> {
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const out: DiscoveredSite[] = [];
  const seen = new Set<string>();

  for (const path of [
    "/v1.0/me/followedSites",
    "/v1.0/sites?search=*",
  ]) {
    try {
      const res = await fetch(`https://graph.microsoft.com${path}`, {
        headers,
      });
      if (!res.ok) continue;
      const json = (await res.json()) as { value?: unknown[] };
      for (const item of json.value ?? []) {
        const site = siteFromGraphRow(item as Record<string, string>);
        if (site === null) continue;
        if (seen.has(site.id)) continue;
        seen.add(site.id);
        out.push(site);
      }
    } catch {
      // Network blip / 401 from one of the endpoints — try the next.
    }
  }
  return out;
}

/**
 * Grant the Repancy app per-site `read` permission on a single site
 * via Graph `POST /sites/{id}/permissions`. Returns the grantId so
 * the caller can persist it for later revocation.
 *
 * If the call fails, throws. The caller wraps in try/catch + surfaces
 * an inline error to the user.
 */
async function grantRepancyAppPermission(
  token: string,
  siteId: string,
  msAppClientId: string,
): Promise<string> {
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/permissions`;
  const body = {
    roles: ["read"],
    grantedToIdentities: [
      {
        application: {
          id: msAppClientId,
          displayName: "Repancy (dev)",
        },
      },
    ],
  };
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "<no body>");
    throw new Error(`grant failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { id?: string };
  if (!json.id) throw new Error("grant returned no id");
  return json.id;
}

/**
 * Revoke a previously-granted permission via Graph
 * `DELETE /sites/{id}/permissions/{grantId}`.
 */
async function revokeRepancyAppPermission(
  token: string,
  siteId: string,
  grantId: string,
): Promise<void> {
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/permissions/${grantId}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  // 204 No Content or 404 (already gone) are both acceptable.
  if (!res.ok && res.status !== 404) {
    const text = await res.text().catch(() => "<no body>");
    throw new Error(`revoke failed: ${res.status} ${text.slice(0, 200)}`);
  }
}

/**
 * Lightweight, dependency-free "are we signed in to Microsoft"
 * indicator. Reads the MSAL active-account; no network call.
 */
function useMicrosoftSignedIn(): { signedIn: boolean; account: { name?: string; username?: string } | null } {
  const [state, setState] = useState<{
    signedIn: boolean;
    account: { name?: string; username?: string } | null;
  }>(() => {
    const a = getActiveAccount();
    return { signedIn: a !== null, account: a };
  });
  useEffect(() => {
    // Re-check on focus — MSAL popup completes async + we want the UI
    // to flip without a hard refresh.
    function recheck(): void {
      const a = getActiveAccount();
      setState({ signedIn: a !== null, account: a });
    }
    window.addEventListener("focus", recheck);
    return () => window.removeEventListener("focus", recheck);
  }, []);
  return state;
}

export function ConnectMySharePointSitesPanel(): JSX.Element {
  const { user } = useUser();
  const msState = useMicrosoftSignedIn();

  const liveSites = useQuery(api.userSelectedSites.listForUser, {});
  const connectedSites = liveSites ?? [];

  const recordGrant = useMutation(api.userSelectedSites.recordGrant);
  const removeGrant = useMutation(api.userSelectedSites.removeGrant);
  // K.11.2 — server-side reconcile action (Node runtime). PATCHes
  // Graph /sites/{id}/permissions/{grantId} with the app-only token
  // because delegated tokens can't mutate site permissions per
  // https://learn.microsoft.com/en-us/graph/api/site-update-permission.
  const upgradeSitePermission = useAction(
    api.userSelectedSitesPermissionNode.upgradeSitePermission,
  );

  const [discoveredSites, setDiscoveredSites] = useState<DiscoveredSite[]>([]);
  const [discovering, setDiscovering] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [working, setWorking] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const handleSignIn = useCallback(async () => {
    setErrorMessage(null);
    try {
      await connectMicrosoftAccount();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(`Microsoft sign-in failed: ${msg}`);
    }
  }, []);

  const handleDiscover = useCallback(async () => {
    setErrorMessage(null);
    setStatusMessage(null);
    setDiscovering(true);
    try {
      const token = await acquireGraphToken();
      if (token === null) {
        setErrorMessage(
          "Sign in with Microsoft first, then try again.",
        );
        return;
      }
      const sites = await fetchDiscoverableSites(token);
      setDiscoveredSites(sites);
      setStatusMessage(`Found ${sites.length} sites in your tenant.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(`Discover failed: ${msg}`);
    } finally {
      setDiscovering(false);
    }
  }, []);

  const handleConnectSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setErrorMessage(null);
    setStatusMessage(null);
    setWorking(true);
    const MS_APP_CLIENT_ID = import.meta.env.VITE_MSAL_CLIENT_ID as
      | string
      | undefined;
    if (MS_APP_CLIENT_ID === undefined || MS_APP_CLIENT_ID.length === 0) {
      setErrorMessage(
        "Repancy app client ID is not configured — contact your operator.",
      );
      setWorking(false);
      return;
    }
    try {
      const token = await acquireGraphToken();
      if (token === null) {
        setErrorMessage("Microsoft sign-in required.");
        return;
      }
      let connected = 0;
      const errors: string[] = [];
      for (const siteId of selectedIds) {
        const site = discoveredSites.find((s) => s.id === siteId);
        if (site === undefined) continue;
        try {
          const grantId = await grantRepancyAppPermission(
            token,
            site.id,
            MS_APP_CLIENT_ID,
          );
          const args = {
            siteId: site.id,
            siteWebUrl: site.webUrl,
            siteDisplayName: site.displayName,
            hostTenant: site.hostTenant,
            permission: "read" as const,
            grantId,
          };
          await recordGrant(args);
          connected += 1;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          errors.push(`${site.displayName}: ${msg}`);
        }
      }
      setStatusMessage(
        connected > 0
          ? `Connected ${connected} site(s).`
          : "Nothing connected.",
      );
      if (errors.length > 0) {
        setErrorMessage(errors.join(" / "));
      }
      setSelectedIds(new Set());
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(`Connect failed: ${msg}`);
    } finally {
      setWorking(false);
    }
  }, [selectedIds, discoveredSites, recordGrant]);

  const handleDisconnect = useCallback(
    async (siteId: string, grantId: string) => {
      setErrorMessage(null);
      setStatusMessage(null);
      setWorking(true);
      try {
        const token = await acquireGraphToken();
        if (token !== null) {
          try {
            await revokeRepancyAppPermission(token, siteId, grantId);
          } catch (err) {
            // Even if the Graph revoke fails (e.g. grant already gone),
            // remove our local row — user wants disconnect.
            console.warn("revoke failed (proceeding with local delete)", err);
          }
        }
        await removeGrant({ siteId });
        setStatusMessage("Disconnected.");
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setErrorMessage(`Disconnect failed: ${msg}`);
      } finally {
        setWorking(false);
      }
    },
    [removeGrant],
  );

  // K.11.2 — toggle a connected site between read and read-write.
  // The Graph PATCH is server-side (app-only token) because delegated
  // tokens cannot mutate site permissions.
  const handleTogglePermission = useCallback(
    async (
      siteId: string,
      targetPermission: "read" | "readwrite",
    ): Promise<void> => {
      setErrorMessage(null);
      setStatusMessage(null);
      setWorking(true);
      try {
        const result = await upgradeSitePermission({ siteId, targetPermission });
        if (result.ok === false) {
          if (result.reason === "graph-error") {
            // K.11.4 — push the toast with a Learn-more link AND keep
            // the inline message for users who don't see the toast.
            pushConnectedSitePermissionErrorToast({
              message: `Couldn't change site permission. ${result.message}`,
              learnMoreUrl: result.learnMoreUrl,
            });
            setErrorMessage(
              `Permission change failed: ${result.message}`,
            );
          } else {
            setErrorMessage(`Permission change failed: ${result.reason}`);
          }
          return;
        }
        setStatusMessage(
          targetPermission === "readwrite"
            ? "Upgraded to read-write."
            : "Downgraded to read-only.",
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setErrorMessage(`Permission change failed: ${msg}`);
      } finally {
        setWorking(false);
      }
    },
    [upgradeSitePermission],
  );

  function toggleSelected(siteId: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }

  const userName =
    msState.account?.name ?? msState.account?.username ?? "your account";

  return (
    <div
      data-testid={testIds.connectMySharePoint.root}
      className="mt-6 border-t border-[#808080] pt-4"
    >
      <h3 className="mb-2 text-base font-bold text-black">
        Connect my SharePoint sites
      </h3>

      <p className="mb-3 text-sm text-black">
        Sign in with Microsoft to browse and select sites from{" "}
        <strong>your own Microsoft 365 tenant</strong>. We can only see
        sites you <strong>explicitly grant</strong> — Microsoft enforces
        this at the tenant level. Disconnecting a site immediately
        revokes our access. Read-only by default.
      </p>

      {!msState.signedIn ? (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => void handleSignIn()}
            data-testid={testIds.connectMySharePoint.signInButton}
            className="border border-[#404040] bg-[#d0d0d0] px-3 py-1 text-sm text-black hover:bg-[#e0e0e0]"
          >
            Sign in with Microsoft →
          </button>
        </div>
      ) : (
        <div className="mb-3 text-sm text-black">
          Signed in as <strong>{userName}</strong>.{" "}
          <button
            type="button"
            disabled={discovering}
            onClick={() => void handleDiscover()}
            data-testid={testIds.connectMySharePoint.discoverButton}
            className="border border-[#404040] bg-[#d0d0d0] px-2 py-0.5 text-xs text-black hover:bg-[#e0e0e0] disabled:opacity-50"
          >
            {discovering ? "Searching…" : "Find sites"}
          </button>
        </div>
      )}

      {statusMessage !== null && (
        <p className="mb-2 text-xs text-[#006600]">{statusMessage}</p>
      )}
      {errorMessage !== null && (
        <p
          data-testid={testIds.connectMySharePoint.errorMessage}
          className="mb-2 text-xs text-[#aa0000] font-mono break-all"
        >
          {errorMessage}
        </p>
      )}

      {discoveredSites.length > 0 && (
        <div className="mb-4 max-h-48 overflow-y-auto border border-[#808080] bg-white p-2">
          {discoveredSites.map((site) => {
            const alreadyConnected = connectedSites.some(
              (c) => c.siteId === site.id,
            );
            return (
              <label
                key={site.id}
                className={`flex items-center gap-2 px-1 py-0.5 text-xs text-black ${
                  alreadyConnected ? "opacity-50" : "cursor-pointer hover:bg-[#f0f0f0]"
                }`}
                data-testid={testIds.connectMySharePoint.discoveredRow(site.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(site.id)}
                  disabled={alreadyConnected || working}
                  onChange={() => toggleSelected(site.id)}
                />
                <span className="font-mono">{site.hostTenant}</span>
                <span>—</span>
                <span>{site.displayName}</span>
                {alreadyConnected && (
                  <span className="ml-auto text-[#006600]">connected</span>
                )}
              </label>
            );
          })}
        </div>
      )}

      {discoveredSites.length > 0 && selectedIds.size > 0 && (
        <button
          type="button"
          disabled={working}
          onClick={() => void handleConnectSelected()}
          data-testid={testIds.connectMySharePoint.connectButton}
          className="mb-4 border border-[#404040] bg-[#d0d0d0] px-3 py-1 text-sm font-bold text-black hover:bg-[#e0e0e0] disabled:opacity-50"
        >
          {working
            ? "Connecting…"
            : `Connect ${selectedIds.size} selected site${selectedIds.size === 1 ? "" : "s"}`}
        </button>
      )}

      {connectedSites.length > 0 && (
        <div className="mt-4 border-t border-[#808080] pt-3">
          <h4 className="mb-2 text-sm font-bold text-black">
            Currently connected ({connectedSites.length})
          </h4>
          <ul
            data-testid={testIds.connectMySharePoint.connectedList}
            className="space-y-1"
          >
            {connectedSites.map((c) => (
              <li
                key={c._id}
                className="flex items-center gap-2 text-xs text-black"
                data-testid={testIds.connectMySharePoint.connectedRow(c.siteId)}
              >
                <span className="font-bold">✓</span>
                <a
                  href={c.siteWebUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {c.siteDisplayName}
                </a>
                <span className="text-gray-700">— {c.permission}</span>
                {c.permission === "read" ? (
                  <button
                    type="button"
                    disabled={working}
                    onClick={() =>
                      void handleTogglePermission(c.siteId, "readwrite")
                    }
                    data-testid={testIds.connectMySharePoint.upgradeButton(
                      c.siteId,
                    )}
                    className="ml-auto border border-[#404080] bg-[#d0d0f0] px-2 py-0.5 text-xs text-[#000080] hover:bg-[#c0c0f0] disabled:opacity-50"
                    title="Upgrade this site to read-write (lets Repancy save edits back to your SharePoint)."
                  >
                    Upgrade to read-write
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={working}
                    onClick={() =>
                      void handleTogglePermission(c.siteId, "read")
                    }
                    data-testid={testIds.connectMySharePoint.downgradeButton(
                      c.siteId,
                    )}
                    className="ml-auto border border-[#806040] bg-[#f0e0c0] px-2 py-0.5 text-xs text-[#604020] hover:bg-[#e8d8b0] disabled:opacity-50"
                    title="Downgrade this site to read-only (Repancy can still browse but cannot save edits)."
                  >
                    Downgrade to read-only
                  </button>
                )}
                <button
                  type="button"
                  disabled={working}
                  onClick={() => void handleDisconnect(c.siteId, c.grantId)}
                  data-testid={testIds.connectMySharePoint.disconnectButton(
                    c.siteId,
                  )}
                  className="border border-[#a04040] bg-[#f0d0d0] px-2 py-0.5 text-xs text-[#660000] hover:bg-[#f0c0c0] disabled:opacity-50"
                >
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-4 text-[10px] text-gray-700 italic">
        Newly-connected sites default to read-only. Use{" "}
        <strong className="font-bold">Upgrade to read-write</strong> on a
        site to allow Repancy to save edits back; you can downgrade or
        disconnect at any time.
      </p>

      {/* Unused-var suppression for the user prop (reserved for future
          per-user metadata display). */}
      <span className="hidden">{user?.id ?? ""}</span>
    </div>
  );
}
